import React, { Component } from 'react';
import i18next from 'i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import get from 'lodash.get';

import Breakpoints from 'lib/Breakpoints';
import { package as PackagePropType } from 'lib/CustomPropTypes';
import { formatDuration } from 'lib/DateTime';
import { isLeadPackageAdjacent } from 'lib/packageHelper';

import ButtonHoverAnimation from 'components/ButtonHoverAnimation';
import Link from 'components/Link';
import SocialShareInline from 'components/Social/ShareInline';
import VideoPlayer from 'components/VideoPlayer';

import ListItem from './ListItem';
import TitleAndDescription from './TitleAndDescription';

class VideoPackage extends Component {
  static propTypes = {
    content: PackagePropType.isRequired,
    pkgClassName: PropTypes.string,
    packageIndex: PropTypes.number,
  };

  static contextTypes = {
    leadPackageType: PropTypes.string,
    vertical: PropTypes.string,
  };

  static defaultProps = {
    pkgClassName: 'pkg videoPkg',
    packageIndex: null,
  };

  constructor(props) {
    super(props);

    this.state = {
      currentVideo: get(this.props, 'content.items[0].item', {}),
      mobile: false,
      videoPlaying: false,
      infoHidden: false,
    };
  }

  componentDidMount() {
    // eslint-disable-next-line react/no-did-mount-set-state
    this.setState({ mobile: Breakpoints.isS() });
  }

  onAdPlay = () => {
    this.setState({
      infoHidden: true,
    });
  }

  onVideoPlay = () => {
    this.setState({
      videoPlaying: true,
      infoHidden: true,
    });
  }

  onVideoPause = () => {
    this.setState({
      videoPlaying: false,
      infoHidden: false,
    });
  }

  onVideoEnd = (selected) => {
    this.setState({
      currentVideo: selected.item,
    });
  }

  onMouseMove = () => {
    const { infoHidden, videoPlaying } = this.state;

    if (videoPlaying) {
      if (this.isCaptionsCardOpen() && !infoHidden) {
        this.setState({ infoHidden: true });
      } else if (!this.isCaptionsCardOpen() && infoHidden) {
        this.setState({ infoHidden: false });
      }
    }
  }

  onMouseLeave = () => {
    const { videoPlaying } = this.state;

    if (videoPlaying) {
      this.setState({ infoHidden: true });
    }
  }

  getListItem = (item) => {
    const { currentVideo, videoPlaying } = this.state;

    return (
      <ListItem
        content={item}
        key={item.id}
        changeVideo={this.changeVideo}
        playing={videoPlaying}
        active={item.id === currentVideo.id}
      />
    );
  }

  isCaptionsCardOpen = () => {
    if (this.player && this.player.wrapper) {
      const captionCard = document.querySelector('.ndpCaptionOptionsCard', this.player.wrapper);
      if (captionCard && (!captionCard.style.display || captionCard.style.display !== 'none')) {
        return true;
      }
    }
    return false;
  }

  changeVideo = (item) => {
    const { videoPlaying, currentVideo, mobile } = this.state;
    if (mobile) {
      window.location.href = item.url.primary;
    } else {
      // Trigger play
      if (!videoPlaying && item.id === currentVideo.id) {
        this.player.manualPlay();
      }
      this.setState({
        currentVideo: item,
        videoPlaying: true,
      });
    }
  }

  meta = () => {
    const { vertical } = this.context;
    const { currentVideo, infoHidden } = this.state;

    const stylesVideoMeta = classNames(
      'video-package__video-meta',
      { 'video-package__info-hidden': infoHidden },
      ['pl5-m', 'pl6-l', 'pt5'],
    );

    return (
      <div className={stylesVideoMeta}>
        {this.title()}
        {this.duration()}
        <SocialShareInline
          placement="videoPackage"
          content={currentVideo}
          propStyles={{ share: 'video-package__share' }}
          vertical={vertical}
        />
      </div>
    );
  }

  title = () => {
    const { currentVideo } = this.state;
    const { headline: { tease }, url: { primary } } = currentVideo;

    return (
      <h3 className="video-package__video-title founders-cond-m f6-m f7-xl lh-title">
        <Link to={primary}>
          {tease}
        </Link>
      </h3>
    );
  }

  duration = () => {
    const { currentVideo: { duration } } = this.state;
    return (
      <span className="video-package__duration founders-mono f2">
        {formatDuration(duration)}
      </span>
    );
  }

  render() {
    const { currentVideo } = this.state;
    const { content, pkgClassName, packageIndex } = this.props;
    // not using `get` since it doesn't guard against `null` values
    const { items: videos, id, metadata } = (content || {});
    const {
      title,
      description,
      seeAllUrl,
      seeAllText,
      logoUrl,
    } = (metadata || {});
    const { leadPackageType, vertical } = this.context;

    const isLiveVideo = currentVideo && currentVideo.videoType === i18next.t('Non Broadcast Live Video');

    const isLeadPkgAdjacent = packageIndex === 1 && isLeadPackageAdjacent(leadPackageType);

    const containerClasses = classNames(
      pkgClassName,
      'gutter-collapse-m',
      'video-package',
      { 'video-package__lead-package--adjacent': isLeadPkgAdjacent },
    );

    return currentVideo && (
      <section
        data-packageid={id}
        className={containerClasses}
        data-test="video-package__section"
      >
        <div className="video-package__grid">
          <div className="video-package__columns df flex-column">
            <TitleAndDescription
              title={title}
              logoUrl={logoUrl}
              description={description}
              vertical={vertical}
              isLeadPackageAdjacent={isLeadPkgAdjacent}
            />
            <div
              className="video-package__wrapper-outer"
              onMouseMove={this.onMouseMove}
              onMouseLeave={this.onMouseLeave}
            >
              <div className="video-package__video-wrapper">
                <VideoPlayer
                  video={currentVideo}
                  onAdPlay={this.onAdPlay}
                  onVideoPlay={this.onVideoPlay}
                  onVideoPause={this.onVideoPause}
                  onVideoEnd={this.onVideoEnd}
                  onRef={(player) => { this.player = player; }}
                  isLive={isLiveVideo}
                  playlist={[{ videos }]}
                  continuousPlay
                />
              </div>
              {this.meta()}
            </div>
          </div>
          <div className="video-package__columns">
            <div className="video-package__scroll">
              <div className="video-package__list">
                {videos.map(this.getListItem)}
              </div>
            </div>
            {seeAllUrl && (
              <div className="video-package__see-all--wrapper">
                <ButtonHoverAnimation
                  type="link"
                  url={seeAllUrl}
                  title={seeAllText || i18next.t('See All')}
                />
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }
}

export default VideoPackage;
